// Libs
import classNames from 'classnames/bind';
import { Box, InputLabel, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import ModalFormTaskJob from '~/components/module/job/modalFormTaskJob/ModalFormTaskJob';
import { ChangeStatusTask } from '~/components/module/task';
// Others
import { DateFormatEnum, RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPaginationResponse } from '~/utils/interface/base';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IQueryParamTask, ITask } from '~/utils/interface/task';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
} from '~/utils/constants/common';
import { formatDate, formatDateByTimeZone, formatTitleCase, getFullName } from '~/utils/helper/common';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import useDebounce from '~/utils/hooks/useDebounce';
import { deleteTask, getTaskList } from '~/thunks/task/taskThunk';
import { selectIsRefreshTaskList, taskActions } from '~/thunks/task/taskSlice';
import { selectJobDetail } from '~/thunks/job/jobSlice';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './JobTask.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onChangeStatus: (task: ITask) => void
): IHeaderCellType<ITask>[] => {
  return [
    {
      id: 'title',
      label: t('table_head_title_name'),
      render: (record: ITask) => record?.title || EMPTY_STRING,
    },
    {
      id: 'assignee',
      label: t('table_head_title_employee'),
      render: (record) => {
        const { assignee } = record;
        if (!assignee) return;

        const { avatarImage, firstName, lastName } = assignee;
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'date',
      label: t('table_head_title_due_date'),
      render: (record: ITask) => {
        return record?.dueDate ? formatDateByTimeZone(record?.dueDate, DateFormatEnum.DD_MMM_YYYY, record?.timeZone) : EMPTY_STRING;
      },
    },
    {
      id: 'status',
      label: t('table_head_title_status'),
      render: (record: ITask) => (record?.status ? <BaseStatus label={record?.status} /> : EMPTY_STRING),
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: ITask) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
            onChangeStatus={() => onChangeStatus(record)}
          />
        );
      },
    },
  ];
};

const JobTask = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshTaskList = useSelector(selectIsRefreshTaskList);
  const jobDetail = useSelector(selectJobDetail);
  const [currentRole] = useState<string | null>(localStorage.getItem(StorageEnum.ROLE));
  //#endregion Selector

  //#region Declare State
  const [taskList, setTaskList] = useState<ITask[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [dataIdTask, setDataIdTask] = useState<string>(EMPTY_STRING);
  const [taskSelected, setTaskSelected] = useState<ITask | null>(null);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [paramObject, setParamObject] = useState<IQueryParamTask>({
    page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    jobId: jobId || EMPTY_STRING,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const [filterParams, setFilterParams] = useState<IQueryParamTask>(FILTER_DEFAULT_VALUES);
  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [isDisplayFormTask, setIsDisplayFormTask] = useState<boolean>(false);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const [pageLimit] = useState<number>(DEFAULT_LIMIT_PAGE);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const debounceSearchKeyTask = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isOpenChangeStatus, setIsOpenChangeStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetTaskList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTaskList) return;

    handleGetTaskList(paramObject);
    dispatch(taskActions.setRefreshTaskList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTaskList]);

  useEffect(() => {
    handleGetEmployeeList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceEmployeeSearchKey]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKeyTask) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: DEFAULT_LIMIT_PAGE,
        ...(debounceSearchKeyTask ? { textSearch: debounceSearchKeyTask } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKeyTask]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleViewTask = (taskId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.tasks}/${taskId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.tasks}/${taskId}`);
        break;
    }
  };

  const handleEditTask = (taskId: string) => {
    setDataIdTask(taskId);
    setIsDisplayFormTask(true);
  };

  const handleDeleteTask = (id: string) => {
    if (!id) return;

    setDataIdTask(id);
    setIsOpenConfirm(true);
  };

  const handleChangeStatus = (task: ITask) => {
    setTaskSelected(task);
    setIsOpenChangeStatus(true);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleRowTableClick = (taskId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.tasks}/${taskId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.tasks}/${taskId}`);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
  };

  const handleOpenFormTask = () => {
    setIsDisplayFormTask(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleApplyFilter = (values: IQueryParamBase | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const handleFilterEmployeeChange = (
    options: string[],
    onChange?: (value: IFilterValueChange<IQueryParamTask>) => void
  ) => {
    if (!options) return;

    onChange?.({ name: 'employeeIds', value: options });
  };

  const handleGetEmployeeList = () => {
    setIsLoadingEmployee(true);

    const params: IQueryParamBase = {
      page: DEFAULT_NUMBER_ONE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };
    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        const dataEmployee: IBaseOption[] = responses.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setEmployeeList(dataEmployee);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };

  const handleSearchEmployee = (keySearch: string) => {
    setEmployeeSearchKey(keySearch);
  };

  const handleCloseModalFormTask = () => {
    setDataIdTask(EMPTY_STRING);
    setIsDisplayFormTask(false);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCloseChangeStatusTask = () => {
    setIsOpenChangeStatus(false);
    setTaskSelected(null);
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    if (!dataIdTask) return;
    setIsLoading(true);

    dispatch(deleteTask(dataIdTask))
      .unwrap()
      .then(() => {
        setDataIdTask(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(taskActions.setRefreshTaskList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetTaskList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getTaskList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setTaskList(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='JobTaskTabComponent' className={cx('container')}>
      <BaseToolbar textAction={t('button_add_task')} onAction={handleOpenFormTask}>
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamTask>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_employee')}</InputLabel>

                <BaseSelect
                  multiple
                  layout='horizontal'
                  width={'175px'}
                  options={employeeList || []}
                  value={value?.employeeIds}
                  onChange={(optionSelected: any) => {
                    handleFilterEmployeeChange(optionSelected, onChange);
                  }}
                  isLoading={isLoadingEmployee}
                  onSearchChange={handleSearchEmployee}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={pageLimit}
          currentPage={pagination?.page}
          count={pagination?.totalPages || DEFAULT_NUMBER_ONE}
          columns={columns(t, handleViewTask, handleEditTask, handleDeleteTask, handleChangeStatus)}
          dataSource={taskList || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isDisplayFormTask && (
        <ModalFormTaskJob
          customerId={jobDetail?.customer?.id || EMPTY_STRING}
          taskId={dataIdTask}
          jobId={jobId || EMPTY_STRING}
          onClose={handleCloseModalFormTask}
        />
      )}

      {isOpenChangeStatus && taskSelected && (
        <ChangeStatusTask onClose={handleCloseChangeStatusTask} task={taskSelected} />
      )}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default JobTask;
